import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HiXCircle, HiArrowTrendingUp } from 'react-icons/hi2';
import { setShowStorageLimitModal } from '../../../redux/slices/storageSlice';
import { StorageLimitError } from '../../../types/storage';
import { formatFileSize } from '../../../utils/storageUtils';
import Button from '../buttons/Button';

interface StorageLimitModalProps {
  isOpen: boolean;
  error: StorageLimitError | null;
}

export default function StorageLimitModal({
  isOpen,
  error,
}: StorageLimitModalProps): JSX.Element | null {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  if (!isOpen || !error) return null;

  const handleClose = () => {
    dispatch(setShowStorageLimitModal(false));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-primary-900/60 backdrop-blur-sm p-4">
      <div className="bg-secondary-50 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-5 border-b border-secondary-200 bg-danger/5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-danger rounded-lg">
              <HiXCircle className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-primary-900">{t('page.files.storageLimitModal.title')}</h3>
          </div>
        </div>

        <div className="px-6 py-5">
          <p className="text-primary-900 font-medium mb-4">
            {t('page.files.storageLimitModal.message')}
          </p>

          <div className="bg-secondary-50 rounded-xl p-4 space-y-3 text-sm border border-secondary-200">
            <div className="flex justify-between items-center">
              <span className="text-primary-900/60">{t('page.files.storageLimitModal.currentUsage')}</span>
              <span className="font-semibold text-primary-900">
                {formatFileSize(error.usedStorage)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-primary-900/60">{t('page.files.storageLimitModal.storageLimit')}</span>
              <span className="font-semibold text-primary-900">
                {formatFileSize(error.maxStorage)}
              </span>
            </div>
            <div className="border-t border-secondary-200 pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-primary-900/60">{t('page.files.storageLimitModal.attemptedUpload')}</span>
                <span className="font-semibold text-danger">
                  {formatFileSize(error.attemptedUpload)}
                </span>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-primary-900/60">{t('page.files.storageLimitModal.additionalSpaceNeeded')}</span>
              <span className="font-semibold text-danger">
                {formatFileSize(error.attemptedUpload - (error.maxStorage - error.usedStorage))}
              </span>
            </div>
          </div>

          <div className="mt-4 p-4 rounded-xl border border-primary bg-primary-100/30">
            <p className="text-sm text-primary-900">
              💡 <strong className="font-semibold">{t('page.files.storageLimitModal.tip')}</strong> {t('page.files.storageLimitModal.tipMessage')}
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-secondary-200 bg-secondary-50 flex justify-end gap-3">
          <Button
            type='button'
            variant="tertiary"
            onClick={handleClose}
          >
            {t('page.files.storageLimitModal.close')}
          </Button>
          <Button
            type='button'
            variant="primary"
            onClick={() => {
              window.location.href = '/subscription';
            }}
          >
            <HiArrowTrendingUp className="w-4 h-4" />
            {t('page.files.storageLimitModal.upgradePlan')}
          </Button>
        </div>
      </div>
    </div>
  );
}
